/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/i18n/fr.aiGov.ts
 *
 * Messages de la gouvernance de l'IA (cahier des charges CV Matching,
 * paragraphe 6) : section « Fonctions » de l'écran /admin/ai-config, choix
 * du modèle des fournisseurs, bandeau d'avertissement. Préfixe `aiGov.`,
 * fusionné dans fr.ts comme fr.offres.ts.
 *
 * Mikael - chef de projet - 2026-09-20
 */
export const messagesAiGovFr: Record<string, string> = {
  'aiGov.bandeau':
    'Aucune fonction n’utilise l’IA tant qu’elle n’est pas activée ici. Chaque fonction a son interrupteur, son fournisseur, son budget et sa consommation.',
  'aiGov.tab.features': 'Fonctions',
  'aiGov.tab.providers': 'Fournisseurs',

  'aiGov.features.titre': 'Fonctions IA',
  'aiGov.features.intro':
    'Le registre des fonctions est déclaré dans le code : une fonction absente de cette liste ne peut pas appeler l’IA. Tout est désactivé par défaut.',
  'aiGov.features.chargement': 'Chargement des fonctions…',
  'aiGov.features.erreur': 'Impossible de charger les fonctions IA. Réessayez dans un instant.',
  'aiGov.features.masterOff':
    'Le module IA est éteint (feature flag ai.recommendations) : aucune fonction ne peut appeler l’IA, même activée ici.',

  'aiGov.fonction.activee': 'Activée',
  'aiGov.fonction.desactivee': 'Désactivée',
  'aiGov.fonction.interrupteur': 'Activer la fonction {libelle}',
  'aiGov.fonction.envoi': 'Ce qui est envoyé au fournisseur',
  'aiGov.fonction.fournisseur': 'Fournisseur',
  'aiGov.fonction.fournisseurAucun': 'Choisir un fournisseur',
  'aiGov.fonction.fournisseurRequis':
    'Choisissez un fournisseur pour pouvoir activer cette fonction.',
  'aiGov.fonction.aucunFournisseur':
    'Aucun fournisseur n’est déclaré : déposez d’abord une clé dans l’onglet Fournisseurs.',
  'aiGov.fonction.budget': 'Budget mensuel (€)',
  'aiGov.fonction.budgetAide': 'Vide : seul le budget du fournisseur s’applique.',
  'aiGov.fonction.modele': 'Modèle',
  'aiGov.fonction.modeleAide': 'Vide : le modèle du fournisseur.',
  'aiGov.fonction.enregistrer': 'Enregistrer',
  'aiGov.fonction.enregistre': 'Fonction mise à jour.',
  'aiGov.fonction.erreur': 'Impossible de mettre à jour la fonction.',

  'aiGov.garde.titre': 'État',
  'aiGov.garde.prete': 'Prête : la garde laisse passer les appels.',
  'aiGov.garde.registre': 'Inconnue du registre.',
  // 2026-09-28 (Mika : « pourquoi je dois État Désactivée ? ») : ces deux
  // lignes disaient le meme mot pour deux causes differentes, a cote d un
  // interrupteur qui affichait « Activée ». Chacune nomme desormais ce qui
  // est eteint, et dit ou l allumer.
  'aiGov.garde.maitre':
    'L’interrupteur général de l’IA est éteint, en haut de cette page : aucune fonction ne peut appeler, même allumée.',
  'aiGov.garde.desactivee': 'L’interrupteur de cette fonction est éteint : aucun appel ne part.',
  'aiGov.garde.fournisseur': 'Le fournisseur assigné est désactivé.',
  'aiGov.garde.cle': 'Aucune clé d’API n’est déposée pour ce fournisseur.',
  'aiGov.garde.budget': 'Budget atteint : les appels sont refusés jusqu’au mois prochain.',
  'aiGov.garde.consentement': 'Le consentement de la personne sera exigé à chaque appel.',

  'aiGov.conso.titre': 'Consommation',
  'aiGov.conso.jour': 'Aujourd’hui',
  'aiGov.conso.mois': 'Ce mois-ci',
  'aiGov.conso.appels': '{n, plural, =0 {aucun appel} one {# appel} other {# appels}}',
  'aiGov.conso.replis': '{n, plural, =0 {aucun repli} one {# repli} other {# replis}}',
  'aiGov.conso.jetons': '{entree} jetons entrés, {sortie} sortis',
  'aiGov.conso.cout': '{cout} € estimés',
  'aiGov.conso.aucune': 'Aucun appel ce mois-ci.',
  'aiGov.conso.estimation':
    'Le coût est une estimation : jetons relevés multipliés par un tarif par modèle (réglage ai.tarifs_json). Seul le fournisseur connaît ce qu’il facture.',
  'aiGov.conso.alerte': 'Seuil d’alerte de {pct} % du budget de la fonction atteint.',
  'aiGov.conso.dernier': 'Dernier appel : {date}',

  'aiGov.provider.modele': 'Modèle utilisé',
  'aiGov.provider.modelePlaceholder': 'Identifiant du modèle, ex. claude-haiku-4-5',
  'aiGov.provider.modeleEnregistrer': 'Enregistrer le modèle',
  'aiGov.provider.modeleAide':
    'Le modèle par défaut des appels de ce fournisseur. Une fonction peut le surcharger dans l’onglet Fonctions.',
  'aiGov.provider.modeleEnregistre': 'Modèle enregistré.',
  'aiGov.provider.modeleErreur': 'Impossible d’enregistrer le modèle.',
  'aiGov.provider.tester': 'Tester la connexion',

  // Vague 5 (2026-09-20) : essai simulé par fonction, journal des prompts.
  'aiGov.essai.bouton': 'Essayer',
  'aiGov.essai.enCours': 'Essai en cours…',
  'aiGov.essai.titre': 'Essai simulé de {libelle}',
  'aiGov.essai.intro':
    'Exécution sur un exemple embarqué, avec un fournisseur simulé : rien ne part, rien n’est compté. Voici ce qui aurait été envoyé et ce qui serait revenu.',
  'aiGov.essai.envoye': 'Ce qui aurait été envoyé',
  'aiGov.essai.systeme': 'Consigne (prompt système)',
  'aiGov.essai.utilisateur': 'Données (prompt utilisateur, anonymisé)',
  'aiGov.essai.revenu': 'Ce qui serait revenu',
  'aiGov.essai.reponse': 'Réponse brute simulée',
  'aiGov.essai.resultat': 'Résultat après validation',
  'aiGov.essai.valide': 'Réponse validée : elle aurait été appliquée.',
  'aiGov.essai.refuse': 'Réponse refusée ({raison}) : l’application aurait continué sans IA.',
  'aiGov.essai.indisponible': 'Aucun essai n’existe pour cette fonction.',
  'aiGov.essai.erreur': 'L’essai n’a pas pu être exécuté.',
  'aiGov.essai.fermer': 'Fermer l’essai',

  'aiGov.logPrompts.titre': 'Journal des prompts',
  'aiGov.logPrompts.interrupteur': 'Journaliser les prompts anonymisés',
  'aiGov.logPrompts.aide':
    'Réglage ai.log_prompts. À 1, chaque prompt envoyé à un fournisseur est copié dans le journal (action ai.prompt) pour vérifier ce qui part. Ce n’est jamais nécessaire en fonctionnement normal : activez-le le temps d’une vérification, puis éteignez-le.',
  'aiGov.logPrompts.actif': 'Journal actif : les prompts sont copiés dans le journal à chaque appel.',
  'aiGov.logPrompts.enregistre': 'Journal des prompts mis à jour.',
  'aiGov.logPrompts.erreur': 'Impossible de modifier le journal des prompts.',
  'aiGov.modele.autre': 'Autre (saisir l’identifiant)',
  'aiGov.modele.autrePlaceholder': 'Identifiant exact du modèle, tel que publié par le fournisseur',
  'aiGov.modele.duFournisseur': 'Modèle du fournisseur (par défaut)',

  // 2026-10-04 : budget global et budget par fonction, calcul en direct,
  // modèle par fonction dans une liste fermée avec son coût indicatif.
  // Mikael - chef de projet - 2026-10-04
  'aiGov.budget.titre': 'Budget mensuel : {fournisseur}',
  'aiGov.budget.intro':
    'Le budget global plafonne la dépense du mois pour ce fournisseur. Les budgets des fonctions se partagent ce global : leur somme ne peut jamais le dépasser. Le calcul suit votre saisie, avant tout enregistrement.',
  'aiGov.budget.global': 'Budget global mensuel (€)',
  'aiGov.budget.globalAide': 'Il ne peut pas descendre sous le total déjà réparti entre les fonctions.',
  'aiGov.budget.enregistrer': 'Enregistrer le budget global',
  'aiGov.budget.enregistre': 'Budget global enregistré.',
  'aiGov.budget.erreur': 'Impossible d’enregistrer le budget global.',
  'aiGov.budget.globalInvalide':
    'Montant invalide : saisissez un nombre positif, avec deux décimales au plus, jusqu’à 10 000 €.',
  'aiGov.budget.globalSousReparti':
    'Trop bas : {reparti} sont déjà répartis entre les fonctions. Baissez d’abord leurs budgets, ou gardez au moins ce montant.',
  'aiGov.budget.chiffreGlobal': 'Budget global',
  'aiGov.budget.chiffreReparti': 'Réparti entre les fonctions',
  'aiGov.budget.chiffreReste': 'Reste disponible',
  'aiGov.budget.depassement':
    'Dépassement de {depassement} : les budgets des fonctions totalisent {reparti} pour un budget global de {global}. Baissez un budget de fonction ou relevez le budget global.',
  'aiGov.budget.jaugeRepartition': 'Part du budget global répartie',
  'aiGov.budget.jaugeDepense': 'Dépense estimée du mois',
  'aiGov.budget.surTotal': '{valeur} sur {total} ({pct} %)',
  'aiGov.budget.parFonction': 'Dépense du mois par fonction',
  'aiGov.budget.depenseSurBudget': '{depense} sur {budget} ({pct} %)',
  'aiGov.budget.depenseSansBudget': '{depense}, sans budget propre',

  'aiGov.fonction.budgetDisponible': 'Disponible pour cette fonction : {disponible}. Vide : aucun budget propre.',
  'aiGov.fonction.budgetInvalide':
    'Montant invalide : saisissez un nombre positif, avec deux décimales au plus, jusqu’à 10 000 €.',
  'aiGov.fonction.budgetDepasse':
    'Dépasse le budget global : {demande} demandés, {disponible} disponibles pour cette fonction. Baissez ce montant ou relevez d’abord le budget global.',

  'aiGov.modele.duFournisseurNomme': 'Modèle du fournisseur ({modele})',
  'aiGov.modele.optionTarif': '{libelle}, {entree} $ / {sortie} $ par million de jetons',
  'aiGov.modele.horsListe': '{modele} (hors liste, conservé tel quel)',
  'aiGov.modele.cout':
    'Coût indicatif de {modele} : {entreeUsd} $ en entrée, {sortieUsd} $ en sortie par million de jetons, soit environ {entreeEur} € et {sortieEur} €. Tarifs vérifiés le {date}. Effet dès l’appel suivant.',
  'aiGov.modele.coutInconnu': 'Coût indicatif inconnu pour ce modèle. Effet dès l’appel suivant.',
};
